"use strict";
/**
 * Venue Status Indicator
 *
 * An unobtrusive corner health dot. Invisible while both venues are healthy;
 * when a venue is impaired it shows a pulsing dot with a tooltip, and clicking
 * opens a small panel naming the impaired venue(s), the reason, and when each
 * was last checked. The panel also lets the operator snooze the dot for 3
 * hours.
 *
 * Dismissal: while the panel is open a transparent full-viewport scrim sits
 * beneath the indicator and catches every outside tap. This is deliberate — a
 * document-level listener alone cannot dismiss the panel when the tap lands on
 * a disabled control (browsers dispatch no click for those at all), which is
 * exactly what the "disable all order buttons" toggle produces.
 *
 * Snooze safety: the snooze is keyed to the impaired-venue set at dismiss
 * time. It suppresses the dot only while the currently-impaired set is a
 * subset of what was snoozed — so a snoozed off-hours warning can never mask a
 * NEW venue going down (the dot reappears immediately for the new venue).
 *
 * Cosmetic only: this never gates order placement.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.impairedVenues = impairedVenues;
exports.shouldShowDot = shouldShowDot;
exports.initVenueStatusIndicator = initVenueStatusIndicator;
const state_1 = require("../state");
const venues_1 = require("../../shared/venues");
const permissions_1 = require("../permissions");
const SNOOZE_STORAGE_KEY = "venue_status_snooze";
const SNOOZE_DURATION_MS = 3 * 60 * 60 * 1000; // 3 hours
const VENUE_LABELS = venues_1.VENUE_STATUS_LABELS;
// ---------------------------------------------------------------------------
// Pure helpers (unit-tested without the DOM)
// ---------------------------------------------------------------------------
/**
 * Sorted list of venue keys that are currently impaired and that this
 * operator can see — a tier without Novig must not learn of it from the dot.
 */
function impairedVenues(status, visible = () => true) {
    if (!status) {
        return [];
    }
    return Object.keys(status)
        .filter((key) => !status[key].healthy && visible(key))
        .sort();
}
const visibleToOperator = (key) => key !== "novig" || (0, permissions_1.canNovigVenue)();
/**
 * Whether the dot should be visible given the impaired set, an optional active
 * snooze, and the current time. Hidden when nothing is impaired, or when every
 * impaired venue is covered by a still-valid snooze.
 */
function shouldShowDot(impaired, snooze, now) {
    if (impaired.length === 0) {
        return false;
    }
    if (snooze && now < snooze.until) {
        const allCovered = impaired.every((v) => snooze.venues.includes(v));
        if (allCovered) {
            return false;
        }
    }
    return true;
}
// ---------------------------------------------------------------------------
// Snooze persistence
// ---------------------------------------------------------------------------
function readSnooze() {
    try {
        const raw = window.localStorage.getItem(SNOOZE_STORAGE_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw);
        if (typeof parsed?.until !== "number" || !Array.isArray(parsed.venues)) {
            return null;
        }
        // Expired snoozes are cleared eagerly so the dot can reappear.
        if (Date.now() >= parsed.until) {
            window.localStorage.removeItem(SNOOZE_STORAGE_KEY);
            return null;
        }
        return parsed;
    }
    catch {
        return null;
    }
}
function writeSnooze(venues) {
    const record = {
        until: Date.now() + SNOOZE_DURATION_MS,
        venues: [...venues].sort(),
    };
    try {
        window.localStorage.setItem(SNOOZE_STORAGE_KEY, JSON.stringify(record));
    }
    catch {
        // If storage is unavailable, silently fall back to no snooze.
    }
}
function clearSnooze() {
    try {
        window.localStorage.removeItem(SNOOZE_STORAGE_KEY);
    }
    catch {
        // no-op
    }
}
// ---------------------------------------------------------------------------
// DOM
// ---------------------------------------------------------------------------
let root = null;
let dotButton = null;
let panel = null;
let scrim = null;
let panelOpen = false;
/** Collapse the panel back to the bare dot. Idempotent. */
function closePanel() {
    if (!panelOpen) {
        return;
    }
    panelOpen = false;
    render();
}
function ensureDom() {
    if (root) {
        return;
    }
    root = document.getElementById("venue-status-indicator");
    if (!root) {
        root = document.createElement("div");
        root.id = "venue-status-indicator";
        document.body.appendChild(root);
    }
    root.classList.add("venue-status-indicator", "hidden");
    dotButton = document.createElement("button");
    dotButton.type = "button";
    dotButton.className = "venue-status-dot";
    dotButton.setAttribute("aria-label", "Venue status");
    dotButton.addEventListener("click", () => {
        panelOpen = !panelOpen;
        render();
    });
    panel = document.createElement("div");
    panel.className = "venue-status-panel hidden";
    root.appendChild(dotButton);
    root.appendChild(panel);
    // Full-viewport click catcher, present only while the panel is open. It sits
    // just below the indicator in the stack, so the dot and panel stay clickable
    // and every OTHER tap lands here.
    //
    // Why a scrim rather than only the document listener below: a disabled
    // <button> dispatches NO click event at all — not on itself, and not on any
    // ancestor — so a tap on a toggled-off order button (the "disable all order
    // buttons" toggle sets the real `disabled` attribute) is invisible to any
    // document-level listener. The scrim intercepts that tap before it can reach
    // the button, which makes dismissal independent of what is underneath.
    scrim = document.createElement("div");
    scrim.className = "venue-status-scrim hidden";
    scrim.addEventListener("click", (event) => {
        event.stopPropagation();
        closePanel();
    });
    document.body.appendChild(scrim);
    // Collapse the panel back to the dot on any click outside it (standard
    // popover behaviour). Capture phase + stopPropagation so the dismissing
    // click is swallowed and does NOT also activate a control behind the panel.
    // Clicks inside the indicator (incl. the dot) fall through untouched.
    document.addEventListener("click", (event) => {
        if (!panelOpen || !root) {
            return;
        }
        if (!root.contains(event.target)) {
            event.stopPropagation();
            closePanel();
        }
    }, true);
    // Escape also collapses it.
    document.addEventListener("keydown", (event) => {
        if (event.key === "Escape") {
            closePanel();
        }
    });
}
function relativeTime(checkedAt) {
    if (!checkedAt) {
        return "not yet checked";
    }
    const secs = Math.max(0, Math.round((Date.now() - new Date(checkedAt).getTime()) / 1000));
    if (secs < 60) {
        return `checked ${secs}s ago`;
    }
    const mins = Math.round(secs / 60);
    return `checked ${mins}m ago`;
}
function venueRow(key, health) {
    const impaired = !health.healthy;
    const stateClass = impaired ? "impaired" : "ok";
    const stateText = impaired ? health.reason ?? "unavailable" : "operational";
    const resume = impaired && health.estimatedResumeAt
        ? ` · resumes ${new Date(health.estimatedResumeAt).toLocaleTimeString()}`
        : "";
    return `
		<div class="venue-status-row ${stateClass}">
			<span class="venue-status-row-dot"></span>
			<span class="venue-status-row-name">${VENUE_LABELS[key]}</span>
			<span class="venue-status-row-state">${stateText}${resume}</span>
			<span class="venue-status-row-checked">${relativeTime(health.checkedAt)}</span>
		</div>`;
}
function renderPanel(status, impaired, snooze) {
    if (!panel) {
        return;
    }
    const rows = Object.keys(status)
        .map((key) => venueRow(key, status[key]))
        .join("");
    let snoozeControls;
    if (snooze && Date.now() < snooze.until) {
        const minsLeft = Math.max(1, Math.round((snooze.until - Date.now()) / 60000));
        snoozeControls = `
			<div class="venue-status-snooze">
				<span class="venue-status-snooze-note">Dismissed for ${minsLeft}m</span>
				<button type="button" class="venue-status-snooze-btn" data-action="unsnooze">Show now</button>
			</div>`;
    }
    else {
        snoozeControls = `
			<div class="venue-status-snooze">
				<button type="button" class="venue-status-snooze-btn" data-action="snooze">Dismiss for 3 hours</button>
			</div>`;
    }
    panel.innerHTML = `
		<div class="venue-status-panel-title">Venue status</div>
		${rows}
		${snoozeControls}`;
    const snoozeBtn = panel.querySelector('[data-action="snooze"]');
    if (snoozeBtn) {
        snoozeBtn.addEventListener("click", () => {
            writeSnooze(impaired);
            closePanel();
        });
    }
    const unsnoozeBtn = panel.querySelector('[data-action="unsnooze"]');
    if (unsnoozeBtn) {
        unsnoozeBtn.addEventListener("click", () => {
            clearSnooze();
            render();
        });
    }
}
function render() {
    ensureDom();
    if (!root || !dotButton || !panel || !scrim) {
        return;
    }
    const status = (0, state_1.getState)().venueStatus;
    const impaired = impairedVenues(status, visibleToOperator);
    const snooze = readSnooze();
    const show = shouldShowDot(impaired, snooze, Date.now());
    if (!show) {
        root.classList.add("hidden");
        panelOpen = false;
        panel.classList.add("hidden");
        scrim.classList.add("hidden");
        return;
    }
    root.classList.remove("hidden");
    const names = impaired.map((v) => VENUE_LABELS[v]).join(", ");
    dotButton.title = `${names} ${impaired.length > 1 ? "are" : "is"} unavailable`;
    if (status) {
        renderPanel(status, impaired, snooze);
    }
    panel.classList.toggle("hidden", !panelOpen);
    // The scrim exists only while the panel is open — it must never sit over a
    // closed UI and eat order-button taps.
    scrim.classList.toggle("hidden", !panelOpen);
}
/** Wire up the corner health-dot indicator. Call once from initApp. */
function initVenueStatusIndicator() {
    ensureDom();
    (0, state_1.subscribe)(() => render());
    render();
}
